import { useCallback } from "react";
import { useSearchParams } from "react-router";
import { Button } from "@designboticteam/react/components";
import { useNotifications } from "@designboticteam/react/features";
import { useEngineApi } from "@/engine";

function downloadFile({ content, fileName, mimeType }: { content: string; fileName: string; mimeType: string }): void {
	const blob = new Blob([content], { type: mimeType });
	const url = URL.createObjectURL(blob);

	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = fileName;
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
	URL.revokeObjectURL(url);
}

const SendToKolegaSection = (): React.ReactNode => {
	const api = useEngineApi();
	const notifications = useNotifications();

	const [params] = useSearchParams();
	const isDebugMode = params.get("debug") !== null;

	const handleOnButtonClick = useCallback(async () => {
		await api.exchange.prepareExport();
		const exportResult = api.exchange.exportProjectArea();

		if (exportResult.hasErrors === true) {
			exportResult.errors.forEach((error) => {
				notifications.add({ type: "warning", message: error });
			});

			return;
		}

		const { payload } = exportResult;
		const message = { type: "EXPORT_JSON", payload };

		if (window.chrome?.webview) {
			console.info("WebView found! Posting message to webview:", { message });
			window.chrome.webview.postMessage(message);
		} else {
			console.info("WebView not found. Posting message to parent window", { message });
			window.parent.postMessage(message, "*");
		}

		notifications.add({ type: "info", message: "Project sent to Kolega" });
	}, [api, notifications]);

	const handleOnDownloadClick = useCallback(async () => {
		await api.exchange.prepareExport();
		const exportResult = api.exchange.exportProjectArea();

		if (exportResult.hasErrors === true) {
			exportResult.errors.forEach((error) => {
				notifications.add({ type: "warning", message: error });
			});

			return;
		}

		const screenshot = await api.maplibre.captureScreenshot({ quality: 0.7 });
		const mapState = api.maplibre.captureMapState();
		const payloadWithDebug = { ...exportResult.payload, _debug: { screenshot, mapState } };

		const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
		downloadFile({ content: JSON.stringify(payloadWithDebug, null, 2), fileName: `kolega-payload-${timestamp}.json`, mimeType: "application/json" });

		notifications.add({ type: "info", message: "Payload saved to file" });
	}, [api, notifications]);

	const handleOnDownloadDxfClick = useCallback(() => {
		const dxf = api.exchange.exportDxf();

		if (dxf === null) {
			notifications.add({ type: "warning", message: "Nothing to export — draw a zone or a building line first" });
			return;
		}

		downloadFile({ content: dxf, fileName: "kolega_export.dxf", mimeType: "application/dxf" });
		notifications.add({ type: "info", message: "DXF saved to file" });
	}, [api, notifications]);

	return (
		<>
			<Button variant="contained" onClick={handleOnButtonClick}>
				<span>
					Send to <strong>Kolega</strong>
				</span>
			</Button>

			<Button variant="outlined" onClick={handleOnDownloadDxfClick}>
				<span>Download DXF</span>
			</Button>

			{isDebugMode && (
				<Button variant="outlined" onClick={handleOnDownloadClick}>
					<span>Download JSON</span>
				</Button>
			)}
		</>
	);
};

export { SendToKolegaSection };
